/**
 * Cross-cluster staff sync — admin control panel.
 *
 * Displays sync configuration (source URL, token status, interval),
 * last pull result per entity, and provides a "Pull now" button that
 * fetches deltas from the configured source cluster.
 */
import React, { useEffect, useState } from "react";
import AppShell from "../../components/layout/AppShell";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { CloudArrowDown, CheckCircle, Warning, ArrowsClockwise } from "@phosphor-icons/react";

export default function DataSync() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [lastPull, setLastPull] = useState(null);

  const load = async () => {
    try {
      const r = await api.get("/sync/status");
      setStatus(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const pullNow = async () => {
    setBusy(true);
    try {
      const r = await api.post("/sync/pull-all");
      setLastPull(r.data);
      // aggregate for toast
      const totals = Object.values(r.data).reduce((acc, v) => {
        if (v && typeof v === "object" && !v.error) {
          acc.created += v.created || 0;
          acc.updated += v.updated || 0;
          acc.skipped += v.skipped || 0;
          acc.excluded_test += v.excluded_test || 0;
        }
        return acc;
      }, { created: 0, updated: 0, skipped: 0, excluded_test: 0 });
      toast.success(
        `Synced across all entities · ${totals.created} created · ${totals.updated} updated · ${totals.excluded_test} test records excluded`,
      );
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  if (!status) return <AppShell title="Data Sync"><div>Loading…</div></AppShell>;

  const configured = status.source_url && status.token_configured;

  return (
    <AppShell title="Cross-cluster Data Sync"
              subtitle="Pull staff records from a source cluster (prod ⇄ builder)">
      <div className="grid md:grid-cols-3 gap-3 mb-8" data-testid="sync-status-cards">
        <ConfigCard label="Source cluster URL"
                    value={status.source_url || "Not configured"}
                    ok={!!status.source_url}
                    hint={!status.source_url ? "Set SYNC_SOURCE_URL in backend/.env on this cluster" : null}/>
        <ConfigCard label="Shared token"
                    value={status.token_configured ? "Configured" : "Missing"}
                    ok={status.token_configured}
                    hint={!status.token_configured ? "Set SYNC_TOKEN (same value on both clusters)" : null}/>
        <ConfigCard label="Auto-pull"
                    value={status.auto_enabled
                            ? `Every ${status.interval_minutes} min`
                            : "Disabled (manual only)"}
                    ok={status.auto_enabled}
                    hint={!status.auto_enabled ? "Set SYNC_AUTO_ENABLED=true to enable" : null}/>
      </div>

      <section className="bg-white border border-[#E5E2D9] rounded-md p-6 mb-8">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-[11px] uppercase tracking-[0.2em] text-[#5C5A55]">Manual pull</div>
            <div className="font-heading text-lg mt-1">All entities · staff, customers, zones, service requests</div>
            <div className="text-xs text-[#8A8780] mt-1">
              Delta pull (idempotent). Test / seed / demo records are auto-excluded — they never leave the source cluster.
            </div>
          </div>
          <button onClick={pullNow} disabled={busy || !configured}
                  data-testid="btn-pull-now"
                  className="inline-flex items-center gap-2 h-10 px-4 bg-[#2D5A27] hover:bg-[#3A7033] disabled:opacity-40 disabled:cursor-not-allowed text-white rounded-md text-sm font-medium">
            {busy
              ? <><ArrowsClockwise size={14} weight="bold" className="animate-spin"/>Pulling…</>
              : <><CloudArrowDown size={14} weight="bold"/>Pull all now</>}
          </button>
        </div>

        {!configured && (
          <div className="flex items-start gap-2 rounded bg-[#FFF6E9] border border-[#E9CBA8] p-3 text-xs text-[#7A5A18]">
            <Warning size={16} weight="fill" color="#D2843C"/>
            <div>
              Sync is not configured on this cluster. Set <code>SYNC_TOKEN</code>{" "}
              (identical across clusters) and <code>SYNC_SOURCE_URL</code>{" "}
              (points at the source cluster) in <code>backend/.env</code>, then restart the backend.
            </div>
          </div>
        )}

        {lastPull && (
          <div className="mt-4 space-y-2">
            {Object.entries(lastPull).map(([entity, r]) => (
              <div key={entity} className="flex items-center justify-between border border-[#E5E2D9] rounded p-3 text-sm" data-testid={`pull-result-${entity}`}>
                <div className="font-medium capitalize">{entity.replace(/_/g, " ")}</div>
                {r.error
                  ? <div className="text-[#C25D38] text-xs">{r.error}</div>
                  : (
                    <div className="flex gap-4 text-xs">
                      <span>fetched <b>{r.fetched}</b></span>
                      <span className="text-[#2D5A27]">created <b>{r.created}</b></span>
                      <span className="text-[#0B2B66]">updated <b>{r.updated}</b></span>
                      <span className="text-[#7A2E18]">skipped <b>{r.skipped}</b></span>
                      <span className="text-[#5C5A55]">test excluded <b>{r.excluded_test || 0}</b></span>
                    </div>
                  )}
              </div>
            ))}
          </div>
        )}
      </section>

      <section>
        <div className="text-[11px] uppercase tracking-[0.2em] text-[#5C5A55] mb-3">Sync history</div>
        <div className="bg-white border border-[#E5E2D9] rounded-md overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[#F7F6F2] text-left text-[11px] uppercase tracking-[0.2em] text-[#5C5A55]">
                <th className="px-6 py-3">Entity</th>
                <th className="px-6 py-3">Last sync</th>
                <th className="px-6 py-3">Fetched</th>
                <th className="px-6 py-3">Created</th>
                <th className="px-6 py-3">Updated</th>
                <th className="px-6 py-3">Skipped</th>
                <th className="px-6 py-3">Test excluded</th>
                <th className="px-6 py-3">Source</th>
              </tr>
            </thead>
            <tbody>
              {(status.entities || []).length === 0 && (
                <tr><td colSpan={8} className="px-6 py-12 text-center text-[#8A8780]">
                  No syncs performed yet.
                </td></tr>
              )}
              {(status.entities || []).map((e, i) => (
                <tr key={e.entity} className={i % 2 === 0 ? "bg-white" : "bg-[#FAFAF7]"}>
                  <td className="px-6 py-3 font-medium capitalize">{e.entity?.replace(/_/g, " ")}</td>
                  <td className="px-6 py-3 text-xs">{e.last_sync_at || "—"}</td>
                  <td className="px-6 py-3 font-mono">{e.last_fetched || 0}</td>
                  <td className="px-6 py-3 font-mono text-[#2D5A27]">{e.last_created || 0}</td>
                  <td className="px-6 py-3 font-mono text-[#0B2B66]">{e.last_updated || 0}</td>
                  <td className="px-6 py-3 font-mono text-[#7A2E18]">{e.last_skipped || 0}</td>
                  <td className="px-6 py-3 font-mono text-[#5C5A55]">{e.last_excluded_test || 0}</td>
                  <td className="px-6 py-3 text-xs text-[#8A8780]">{e.source_url || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </AppShell>
  );
}

function ConfigCard({ label, value, ok, hint }) {
  return (
    <div className="bg-white border border-[#E5E2D9] rounded-md p-5">
      <div className="text-[10px] uppercase tracking-[0.2em] text-[#5C5A55] mb-2">{label}</div>
      <div className="flex items-center gap-2">
        {ok
          ? <CheckCircle size={16} weight="fill" color="#2D5A27"/>
          : <Warning size={16} weight="fill" color="#C25D38"/>}
        <div className="text-sm font-medium">{value}</div>
      </div>
      {hint && <div className="text-[10px] text-[#8A8780] mt-2">{hint}</div>}
    </div>
  );
}
